import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { useCompanyUser } from "@bokio/shared/state/requests";

type FeedbackTarget = "Support" | "Metrics";

export const useUserFeedback = (
	onSuccess?: (
		data: m.Settings.Controllers.HelpCreateResult,
		params: [string, m.Contracts.HelpRequest, (Blob | string)[]],
	) => void,
) => {
	const [sendFeedbackApi, sendFeedbackRequest] = useLazyApi(proxy.Settings.HelpController.Create.Post, { onSuccess });
	const [saveFeedback, saveFeedbackRequest] = useLazyApi(
		proxy.Bokio.Common.Web.Metrics.MetricsController.SaveGenericFeedback.Post,
	);
	const { companyInfo } = useCompanyInfo();
	const { userStatus } = useCompanyUser();

	/**
	 * Sends user feedback to specified targets (Support and/or Metrics).
	 *
	 * @param message - The feedback message content
	 * @param subject - This is the category of the feedback
	 * @param topic - This is the subcategory of the feedback
	 * @param targets - Optional array specifying where to send the feedback.
	 *                  Can include "Support" (sends to support API) and/or "Metrics" (saves to metrics).
	 *                  Defaults to both ["Support", "Metrics"] if not specified.
	 */
	const sendFeedback = (message: string, subject: string, topic: string, targets?: FeedbackTarget[]) => {
		const feedbackTargets: FeedbackTarget[] = targets ?? ["Support", "Metrics"];
		if (!feedbackTargets.length) {
			throw Error("At least one feedback target must be set");
		}

		if (!!feedbackTargets.find(x => x === "Metrics")) {
			saveFeedback(companyInfo.Id, { Topic: topic, Subject: subject, Message: message });
		}

		if (!!feedbackTargets.find(x => x === "Support")) {
			sendFeedbackApi(
				companyInfo.Id,
				{
					FrontendRoute: window.location.href,
					ExactUrl: window.location.pathname + window.location.search,
					IsCurrentPageRelevant: false,
					Email: userStatus?.Email ?? "",
					Name: userStatus?.Name ?? "",
					Message: message,
					Subject: subject,
					Type: m.Contracts.HelpType.Feedback,
					Area: m.Contracts.SupportFormArea.Other,
					Category: m.Contracts.SupportFormCategory.NotSet,
					SubCategory: m.Contracts.SupportFormSubCategory.NotSet,
					ReferenceId: "",
					PhoneNumber: "",
				},
				[],
			);
		}
	};

	const isSaving = sendFeedbackRequest?.isLoading || saveFeedbackRequest?.isLoading;

	return { sendFeedback, sendFeedbackRequest, saveFeedbackRequest, isSaving };
};
